import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Stack, useTheme } from "@mui/material";

import useGetHotelExtras from "../../hooks/hotelExtras/useGetHotelExtras";
import useDeleteHotelExtra from "../../hooks/hotelExtras/useDeleteHotelExtra";
import useDeleteAllHotelExtras from "../../hooks/hotelExtras/useDeleteAllHotelExtras";
import useRestoreHotelExtra from "../../hooks/hotelExtras/useRestoreHotelExtra";
import useRestoreAllHotelExtras from "../../hooks/hotelExtras/useRestoreAllHotelExtras";
import useForceDeleteHotelExtra from "../../hooks/hotelExtras/useForceDeleteHotelExtra";
import useForceDeleteAllHotelExtras from "../../hooks/hotelExtras/useForceDeleteAllHotelExtras";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import HotelExtrasTableRow from "./Table/HotelExtrasTableRow";
import HotelExtrasTableColumns from "./Table/HotelExtrasTableColumns";
import ArchiveButton from "../global/Table/ArchiveButton";
import toast from "react-hot-toast";
import { usePermissions } from "../../auth/PermissionsContext";

const HotelExtrasTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("hotel_extras_list"),
            create: has("hotel_extras_create"),
            edit: has("hotel_extras_edit"),
            delete: has("hotel_extras_delete"),
            forceDelete: has("hotel_extras_force_delete"),
            archiveList: has("hotel_extras_archive_list"),
            restore: has("hotel_extras_restore"),
            restoreAll: has("hotel_extras_restore-all-table"),
            deleteAll: has("hotel_extras_delete-all-table"),
            massForceDeleteAll: has("hotel_extras_mass_force_delete-all-table"),
        }),
        [has]
    );

    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const isArchive = location.pathname.includes("/hotel-extras/archive");

    const { deleteHotelExtra, isDelete } = useDeleteHotelExtra();
    const { deleteAllHotelExtras, isDeleteAll } = useDeleteAllHotelExtras();
    const { restoreHotelExtra, isRestore } = useRestoreHotelExtra();
    const { restoreAllHotelExtras, isRestoreAll } = useRestoreAllHotelExtras();
    const { forceDeleteHotelExtra, isForceDelete } = useForceDeleteHotelExtra();
    const { forceDeleteAllHotelExtras, isForceDeleteAll } = useForceDeleteAllHotelExtras();

    const {
        page,
        perPage,
        search,
        sortBy,
        sortDir,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
        setSelectedIds,
    } = useTableActions({
        basePath: "/hotel-extras",
        deleteSingle: deleteHotelExtra,
        deleteAll: deleteAllHotelExtras,
        restoreSingle: restoreHotelExtra,
        restoreAll: restoreAllHotelExtras,
        forceDeleteSingle: forceDeleteHotelExtra,
        forceDeleteAll: forceDeleteAllHotelExtras,
        isArchive,
    });

    const [statusFilter, setStatusFilter] = React.useState("");

    const filters = useMemo(
        () => ({
            search: search || undefined,
            status: statusFilter || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
            ...(isArchive ? { only_trashed: true } : {}),
        }),
        [search, statusFilter, sortBy, sortDir, isArchive]
    );

    const filterConfig = useMemo(
        () => [
            {
                type: "select",
                name: "status",
                label: { ar: "الحالة", en: "Status" },
                options: [
                    { value: "", label: { ar: "الكل", en: "All" } },
                    { value: "active", label: { ar: "نشط", en: "Active" } },
                    { value: "disabled", label: { ar: "معطل", en: "Disabled" } },
                ],
                value: statusFilter,
                onChange: setStatusFilter,
            },
        ],
        [statusFilter]
    );

    const handleClearAllFilters = () => {
        setStatusFilter("");
    };

    const { isLoading, error, hotelExtras, meta } = useGetHotelExtras(
        page,
        perPage,
        filters
    );

    const items = useMemo(() => hotelExtras?.items || [], [hotelExtras]);

    const rowMenuDisabled = isArchive
        ? !perms.restore && !perms.forceDelete
        : !perms.edit && !perms.delete;

    const renderRow = (params) => (
        <HotelExtrasTableRow
            key={params.item.id}
            hotelExtra={params.item}
            index={params.index}
            isDark={isDark}
            page={params.page}
            perPage={params.perPage}
            sortBy={params.sortBy}
            sortDir={params.sortDir}
            totalItems={items.length}
            selectedIds={selectedIds}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
            isArchive={isArchive}
            menuDisabled={rowMenuDisabled}
        />
    );

    const bulkDeleteDisabled = isArchive
        ? !perms.massForceDeleteAll
        : !perms.deleteAll;

    const title = isArabic ? "إضافات الفنادق" : "Hotel Extras";
    const subtitle = isArchive
        ? isArabic
            ? "عرض إضافات الفنادق المحذوفة (الأرشيف)"
            : "Archived hotel extras (soft deleted)"
        : isArabic
            ? "إدارة إضافات الفنادق المتاحة في النظام"
            : "Manage available hotel extras in the system";

    const addButtonDisabled = isArchive
        ? !perms.restoreAll || selectedIds.length === 0
        : !perms.create;

    const addButtonLabel = isArchive
        ? isRestoreAll
            ? isArabic
                ? "جاري الاستعادة..."
                : "Restoring..."
            : isArabic
                ? "استعادة المحدد"
                : "Restore selected"
        : isArabic
            ? "إضافة إضافة فندق"
            : "Create new hotel extra";

    const addButtonAction = isArchive
        ? () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic
                        ? "ليس لديك صلاحية للاستعادة"
                        : "No restore permission"
                );
                return;
            }

            if (isRestoreAll) return;

            if (!selectedIds.length) {
                toast.error(
                    isArabic
                        ? "الرجاء تحديد العناصر المراد استرجاعها أولاً"
                        : "Please select items to restore first"
                );
                return;
            }

            restoreAllHotelExtras({ items: selectedIds });
            setSelectedIds([]);
        }
        : () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic
                        ? "ليس لديك صلاحية للإنشاء"
                        : "No create permission"
                );
                return;
            }

            navigate("/hotel-extras/create");
        };

    return (
        <>
            {!isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/hotel-extras/archive"
                        visible={perms.archiveList}
                        disabled={!perms.archiveList}
                        archiveLabelAr="الأرشيف"
                        archiveLabelEn="Archive"
                    />
                </Stack>
            )}
            {isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/hotel-extras"
                        visible={true}
                        archiveLabelAr="العودة للقائمة"
                        archiveLabelEn="Back to list"
                    />
                </Stack>
            )}
            <TableLayout
                title={title}
                subtitle={subtitle}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={addButtonLabel}
                onAddClick={addButtonAction}
                addButtonVisible={true}
                isLoading={isLoading}
                error={error}
                items={items}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                sortBy={sortBy}
                sortDir={sortDir}
                onRequestSort={handleRequestSort}
                columns={HotelExtrasTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuDelete={handleMenuDelete}
                onMenuRestore={isArchive ? handleMenuRestore : undefined}
                onMenuEdit={!isArchive ? handleEdit : undefined}
                menuDisableEdit={!isArchive && !perms.edit}
                menuDisableRestore={isArchive && !perms.restore}
                menuDisableDelete={isArchive ? !perms.forceDelete : !perms.delete}
                isDeletingSingle={isArchive ? isRestore || isForceDelete : isDelete}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, items)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                bulkDeleteDisabled={bulkDeleteDisabled}
                isBulkDeleting={isArchive ? isForceDeleteAll : isDeleteAll}
                addButtonDisabled={addButtonDisabled}
                addButtonTooltip={
                    addButtonDisabled
                        ? isArabic
                            ? "لا تملك صلاحية تنفيذ هذا الإجراء"
                            : "You don't have permission"
                        : undefined
                }
                emptyLabelAr={
                    isArchive ? "لا توجد إضافات فنادق في الأرشيف" : "لا توجد إضافات فنادق"
                }
                emptyLabelEn={isArchive ? "No archived hotel extras" : "No hotel extras"}
                loadErrorAr="حدث خطأ أثناء تحميل إضافات الفنادق"
                loadErrorEn="Failed to load hotel extras"
                filters={filterConfig}
                onClearAllFilters={handleClearAllFilters}
            />
        </>
    );
};

export default HotelExtrasTable;
